import { useEffect } from 'react'

import { useGameSupplierAvailable } from 'hooks/useGameSupplierAvailable'
import { useIsGameGeoBlocked } from 'hooks/useIsGameGeoBlocked'
import { useLoadExternalGame } from 'hooks/useLoadExternalGame'
import { useAppDispatch } from 'src/store/hooks'
import {
  cleanUpGame,
  setActiveGameCode,
  setGoldMinBet,
  setSweepstakeMinBet,
} from 'src/store/modules/slotGameFlow/actions'

import GameBackground from '../components/GameBackground'
import GameBlockedByLocation from '../components/GameBlockedByLocation'

import type { TempToken } from '@patrianna/shared-patrianna-types/services/tokens'
import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'
import type { ReactNode } from 'react'
import type { SupportedCurrencies } from 'types/currencies'

type Props = {
  gameProduct: GameProduct
  currency: SupportedCurrencies
  children: (gameProduct: GameProduct, currency: SupportedCurrencies, slotToken: TempToken) => ReactNode
}

export default function GameInitializationWrapper({ currency, gameProduct, children }: Props) {
  const dispatch = useAppDispatch()
  // PLDV-8952: geo-blocked games skip the token request entirely, so no game
  // session is opened for a state where the game must not be playable.
  const isGeoBlocked = useIsGameGeoBlocked(gameProduct.code)
  const { slotToken, loading } = useLoadExternalGame(gameProduct, currency, isGeoBlocked)
  const gameSupplierAvaliable = useGameSupplierAvailable(gameProduct.supplier)

  useEffect(() => {
    dispatch(setActiveGameCode({ code: gameProduct.code }))
    dispatch(setGoldMinBet({ goldMinBet: gameProduct.goldMinBet }))
    dispatch(setSweepstakeMinBet({ sweepstakeMinBet: gameProduct.sweepstakeMinBet }))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameProduct.code])

  useEffect(() => {
    return () => {
      dispatch(cleanUpGame())
    }
  }, [])

  if (isGeoBlocked) {
    return (
      <GameBackground gameProduct={gameProduct}>
        <GameBlockedByLocation />
      </GameBackground>
    )
  }

  if (loading || !slotToken) {
    return <GameBackground gameProduct={gameProduct} />
  }

  if (!gameSupplierAvaliable) {
    return (
      <GameBackground gameProduct={gameProduct}>
        <GameBlockedByLocation />
      </GameBackground>
    )
  }

  return <>{children(gameProduct, currency, slotToken)}</>
}
